import { React, _ } from '../../lib';
export const ConditionalObject = (cond, obj) => {
    if (!cond) {
        return undefined;
    }
    if (obj instanceof Function) {
        return obj();
    }
    return obj;
};
export const CJSX = (props) => (props.cond ? React.createElement(React.Fragment, null, props.children) : React.createElement(React.Fragment, null));
export const CopyState = (val) => _.clone(val);
export const Bindings = (obj, methods) => {
    _.forEach(methods, method => {
        if (method.name.startsWith('bound ')) {
            console.warn('Function is already bound', method);
        }
        else {
            obj[method.name] = method.bind(obj);
        }
    });
};
export const useMemoCompare = (valueFn, nextCompare, compareFn = (p, n) => _.isEqualWith(p, n, _.isEqual)) => {
    const previousRefValue = React.useRef(null);
    const previousRefCompare = React.useRef(null);
    const hasRunOnce = React.useRef(false);
    if (!hasRunOnce.current) {
        const valueUnpacked = valueFn();
        previousRefCompare.current = { value: nextCompare };
        previousRefValue.current = { value: valueUnpacked };
        hasRunOnce.current = true;
        return valueUnpacked;
    }
    const isEqual = previousRefCompare.current
        ? compareFn(previousRefCompare.current.value, nextCompare)
        : false;
    if (!isEqual || !previousRefValue.current) {
        const valueUnpacked = valueFn();
        previousRefCompare.current = { value: nextCompare };
        previousRefValue.current = { value: valueUnpacked };
        return valueUnpacked;
    }
    return previousRefValue.current.value;
};
export const useEffectCompare = (effect, nextCompare, compareFn = (p, n) => _.isEqualWith(p, n, _.isEqual)) => {
    const counter = React.useRef(1);
    useMemoCompare(() => {
        counter.current++;
        return 1;
    }, nextCompare, compareFn);
    React.useEffect(effect, [counter.current]);
};
export const useStateObj = (defVal) => {
    const [value, onUpdate] = React.useState(defVal);
    return { value, onUpdate };
};
export const useStateSync = (args) => {
    const hasUpdatedOnRender = React.useRef(false);
    const fallback = React.useMemo(() => (a, b) => _.isEqualWith(a, b, _.isEqual), []);
    const compareFn = args.compareFn ?? fallback;
    useEffectCompare(() => {
        if (args.debug) {
            console.log('Change prop effect');
        }
        if (!compareFn(args.propVal, args.stateVal)) {
            if (args.debug) {
                console.log('Prop changed', { prop: args.propVal, state: args.stateVal });
            }
            hasUpdatedOnRender.current = true;
            if (!args.disabled) {
                args.setState(args.propVal);
            }
        }
    }, args.propVal);
    useEffectCompare(() => {
        if (hasUpdatedOnRender.current) {
            return;
        }
        if (args.debug) {
            console.log('Change state effect');
        }
        if (!compareFn(args.propVal, args.stateVal)) {
            if (args.debug) {
                console.log('State changed', { prop: args.propVal, state: args.stateVal });
            }
            if (!args.disabled) {
                args.setProp(args.stateVal);
            }
        }
    }, args.stateVal);
    React.useEffect(() => {
        hasUpdatedOnRender.current = false;
    });
};
export const useStateSimple = (propVal, setProp) => {
    const [value, setValue] = React.useState(propVal);
    useStateSync({
        stateVal: value,
        propVal: propVal,
        setState: setValue,
        setProp: setProp,
        compareFn: (p, s) => p === s,
    });
    return [value, setValue];
};
export const useRSInstance = (input) => {
    const refCheck = React.useRef({
        refs: input.refs,
        defaultState: input.defaultState,
        actionToDelta: input.actionToDelta,
        onStateUpdated: input.onStateUpdated,
    });
    if (refCheck.current.defaultState !== input.defaultState) {
        console.warn('Function `defaultState` changed between renders');
    }
    if (refCheck.current.actionToDelta !== input.actionToDelta) {
        console.warn('Function `actionToDelta` changed between renders');
    }
    const uniqueKeys = new Set([
        ...Object.keys(refCheck.current.refs),
        ...Object.keys(input.refs),
    ]);
    uniqueKeys.forEach(key => {
        if (refCheck.current.refs[key] !== input.refs[key]) {
            console.warn(`Ref object '${key}' changed between renders`);
        }
    });
    const [, setIncrement] = React.useState(0);
    const initialValue = {
        props: input.props,
        state: React.useMemo(() => input.defaultState(input.props), []),
        refs: input.refs,
        dispatch: (payload) => {
            if (!input.actionToDelta) {
                console.warn('No action to delta function provided for dispatch');
                return;
            }
            const delta = input.actionToDelta?.(refInstance.current, payload);
            if (delta) {
                refInstance.current.state = {
                    ...refInstance.current.state,
                    ...delta,
                };
                input.onStateUpdated?.(refInstance.current.state);
                setIncrement(v => v + 1);
            }
        },
        updateState: (delta) => {
            refInstance.current.state = {
                ...refInstance.current.state,
                ...(_.isFunction(delta) ? delta(refInstance.current.state) : delta),
            };
            input.onStateUpdated?.(refInstance.current.state);
            setIncrement(v => v + 1);
        },
    };
    const refInstance = React.useRef(initialValue);
    refInstance.current.props = input.props;
    refInstance.current.refs = input.refs;
    return refInstance.current;
};
export const MatchRender = (props) => {
    const condition = props.check.find(c => c.when);
    return condition?.then ?? React.createElement(React.Fragment, null);
};
//# sourceMappingURL=meta-types.js.map